import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AUTH_QUERY_KEY } from '../auth/useAuth';
import {
  fetchMyAvatars,
  createAvatar,
  updateAvatar,
  deleteAvatar,
  activateAvatar,
  toggleAvatarLike,
  fetchUserAvatars,
  fetchUserProfile,
} from '../../lib/api';
import type { SavedAvatarData, UserProfileData } from '../../lib/api';

const MY_AVATARS_KEY = ['avatars', 'mine'] as const;

function userAvatarsKey(key: string) {
  return ['avatars', 'user', key] as const;
}

function avatarDetailKey(id: number) {
  return ['avatars', 'detail', id] as const;
}

function userProfileKey(key: string) {
  return ['users', 'profile', key] as const;
}

export function useMyAvatars() {
  return useQuery<SavedAvatarData[]>({
    queryKey: MY_AVATARS_KEY,
    queryFn: fetchMyAvatars,
  });
}

export function useUserAvatars(key: string) {
  return useQuery<SavedAvatarData[]>({
    queryKey: userAvatarsKey(key),
    queryFn: () => fetchUserAvatars(key),
    enabled: !!key,
  });
}

export function useUserProfile(key: string) {
  return useQuery<UserProfileData>({
    queryKey: userProfileKey(key),
    queryFn: () => fetchUserProfile(key),
    enabled: !!key,
  });
}

export function useCreateAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; config: Record<string, unknown> }) =>
      createAvatar(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MY_AVATARS_KEY });
    },
  });
}

export function useUpdateAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      name?: string;
      config?: Record<string, unknown>;
    }) => updateAvatar(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: MY_AVATARS_KEY });
      qc.invalidateQueries({ queryKey: avatarDetailKey(vars.id) });
    },
  });
}

export function useDeleteAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteAvatar(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MY_AVATARS_KEY });
      qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
  });
}

export function useActivateAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activateAvatar(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MY_AVATARS_KEY });
      qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
  });
}

export function useToggleAvatarLike() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => toggleAvatarLike(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MY_AVATARS_KEY });
    },
  });
}
